import React, { useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import { printService } from 'api/services/printService';
import type { PrintJob, PrinterLookup } from 'api/types';
import { apiMessage } from 'components/modules/print-module/printUi';

/**
 * "Print elsewhere": pick another active printer for a failed or waiting job.
 * The job becomes REROUTED and a new job prints there, headed REROUTED FROM.
 */
interface Props {
  job: PrintJob | null;
  busy?: boolean;
  error?: string | null;
  onConfirm: (printerId: string) => void;
  onCancel: () => void;
}

const PrintReroutePickerModal: React.FC<Props> = ({ job, busy = false, error, onConfirm, onCancel }) => {
  const [printers, setPrinters] = useState<PrinterLookup[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [printerId, setPrinterId] = useState('');

  useEffect(() => {
    if (!job) return;
    let alive = true;
    setPrinterId(''); setLoadError(null);
    printService.getPrinterLookup()
      .then((p) => { if (alive) setPrinters(p); })
      .catch((err) => { if (alive) setLoadError(apiMessage(err, 'The printers could not be loaded.')); });
    return () => { alive = false; };
  }, [job]);

  const options = useMemo(() => printers.filter((p) => p.id !== job?.printerId).map((p) => ({
    value: p.id,
    label: `${p.printerName} · ${p.agentName ?? 'no agent'}${p.agentOnline ? '' : ' (agent offline)'}${p.agentStatus && p.agentStatus !== 'ACTIVE' ? ` (${p.agentStatus.toLowerCase()})` : ''}`,
    isDisabled: !!p.agentStatus && p.agentStatus !== 'ACTIVE',
  })), [printers, job]);

  if (!job) return null;
  return (
    <>
      <div className="modal-backdrop" style={{ zIndex: 10040 }} />
      <div className="modal-container modal-sm" role="dialog" aria-modal="true" style={{ minHeight: 'auto', maxWidth: 480, zIndex: 10050 }} data-reroute-modal>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Print elsewhere — {job.sourceReference ?? 'job'}</h2>
          <button className="modal-close" onClick={onCancel} disabled={busy} aria-label="Close"><IconSystem.x size={18} /></button>
        </div>
        <div className="modal-content">
          <p style={{ marginTop: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
            The ticket prints on the printer you pick, headed REROUTED FROM {job.printerName ?? 'its printer'}. This job stops.
          </p>
          {(error || loadError) && <div className="form-error-message" style={{ marginBottom: 10 }}>{error ?? loadError}</div>}
          <div className="form-group">
            <SyutSelect label={<>Printer <span className="text-danger">*</span></>} options={options} value={printerId} size="sm"
              menuPortalTarget={document.body} isSearchable placeholder={options.length ? 'Pick a printer' : 'No other active printer'}
              onChange={(v) => setPrinterId((v as string) || '')} />
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onConfirm(printerId)} disabled={busy || !printerId}>{busy ? 'Sending…' : 'Print there'}</button>
        </div>
      </div>
    </>
  );
};

export default PrintReroutePickerModal;
